import OpenAI from 'openai';

// Initialize OpenAI client only if API key is available
let openai: OpenAI | null = null;

if (import.meta.env.VITE_OPENAI_API_KEY) {
  openai = new OpenAI({
    apiKey: import.meta.env.VITE_OPENAI_API_KEY,
    dangerouslyAllowBrowser: true
  });
}

const DOCUMENT_PROMPTS = {
  management: `Crea un plan detallado de documento de gestión educativa que incluya:
- Objetivos de gestión
- Competencias clave
- Cronograma de actividades
- Recursos necesarios
- Criterios de evaluación
- Indicadores de logro
Por favor, formatea la respuesta de manera clara y estructurada.`,

  project: `Crea un plan detallado de proyecto educativo que incluya:
- Objetivos de aprendizaje
- Competencias clave
- Cronograma
- Recursos necesarios
- Criterios de evaluación
Por favor, formatea la respuesta de manera clara y estructurada.`,

  unit: `Crea una unidad de aprendizaje integral que incluya:
- Objetivos de la unidad
- Conceptos clave
- Actividades de aprendizaje
- Materiales necesarios
- Métodos de evaluación
Por favor, formatea la respuesta de manera clara y estructurada.`,

  session: `Crea un plan detallado de sesión de clase que incluya:
- Objetivos de la sesión
- Actividades con tiempos
- Materiales requeridos
- Estrategias de evaluación
Por favor, formatea la respuesta de manera clara y estructurada.`,

  evaluation: `Crea un plan de evaluación educativa que incluya:
- Objetivos de evaluación
- Criterios de evaluación
- Instrumentos de evaluación
- Rúbricas de calificación
- Estrategias de retroalimentación
Por favor, formatea la respuesta de manera clara y estructurada.`
};

export type DocumentType = 'management' | 'project' | 'unit' | 'session' | 'evaluation';

export async function generateDocument(
  type: DocumentType, 
  requirements: string, 
  referenceContent?: { content: string; fileName: string }
) {
  // Check if OpenAI is configured
  if (!openai) {
    throw new Error('La clave API de OpenAI no está configurada. Por favor, configúrala en el archivo .env como VITE_OPENAI_API_KEY');
  }

  // Check if curriculum is uploaded
  const curriculumFile = localStorage.getItem('curriculumFile');
  if (!curriculumFile) {
    throw new Error('Por favor, carga el DCN (Diseño Curricular) antes de generar contenido.');
  }

  const systemPrompt = DOCUMENT_PROMPTS[type];
  const messages: Array<{ role: 'system' | 'user'; content: string }> = [
    {
      role: "system",
      content: systemPrompt
    }
  ];

  if (referenceContent) {
    messages.push({
      role: "system",
      content: `Usa el siguiente documento como material de referencia: ${referenceContent.fileName}\n\n${referenceContent.content}`
    });
  }

  // Add curriculum context
  try {
    const curriculumData = JSON.parse(curriculumFile);
    messages.push({
      role: "system",
      content: `Contexto del Diseño Curricular Nacional (DCN): ${curriculumData.name} ha sido cargado como referencia curricular.`
    });
  } catch (e) {
    console.warn('Could not parse curriculum file data');
  }

  messages.push({
    role: "user",
    content: requirements || `Genera un ${type} educativo completo y detallado.`
  });

  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4",
      messages,
      temperature: 0.7,
      max_tokens: 2000
    });

    return response.choices[0].message.content;
  } catch (error: any) {
    console.error('OpenAI API Error:', error);
    
    if (error.code === 'insufficient_quota') {
      throw new Error('Se ha agotado la cuota de la API de OpenAI. Por favor, verifica tu suscripción.');
    } else if (error.code === 'invalid_api_key') {
      throw new Error('La clave API de OpenAI no es válida. Por favor, verifica tu configuración.');
    } else if (error.code === 'rate_limit_exceeded') {
      throw new Error('Se ha excedido el límite de velocidad de la API. Por favor, intenta de nuevo en unos momentos.');
    } else {
      throw new Error(`Error de la API de OpenAI: ${error.message || 'Error desconocido'}`);
    }
  }
}